'use client';

import Image from 'next/image';
import { type JSX, type ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';

import { getAccessibleFocusStyles, Text } from '@videoblocks/design-system';
import { Play, WarningTriangle } from '@videoblocks/react-icons';

import { type AiToolkitSubmitStatus } from '@app/(darkHeader)/ai/_shared/_context/types';

type ActiveStatus = Exclude<AiToolkitSubmitStatus, 'idle'>;

const SUB_LABELS: Record<ActiveStatus, string> = {
    inProgress: 'Generating…',
    success: 'Tap to view',
    error: 'Tap to retry',
};

export interface CurrentTaskCtaProps {
    title: string;
    status: AiToolkitSubmitStatus;
    thumbnailUrl?: string;
    videoUrl?: string;
    placeholderIcon: ReactNode;
    onClick: () => void;
    dataTestId?: string;
}

export const CurrentTaskCta = ({
    title,
    status,
    thumbnailUrl,
    videoUrl,
    placeholderIcon,
    onClick,
    dataTestId = 'current-task-cta',
}: CurrentTaskCtaProps): JSX.Element | null => {
    if (status === 'idle') {
        return null;
    }

    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={dataTestId}
            className={twMerge(
                'flex items-center gap-3 self-end p-1 rounded-sm',
                'w-[211px] h-[51px] shrink-0',
                'dark:bg-neutral-dark-200 hover:opacity-90 transition-opacity cursor-pointer',
                getAccessibleFocusStyles(),
            )}
        >
            <Thumbnail
                title={title}
                status={status}
                videoUrl={videoUrl}
                thumbnailUrl={thumbnailUrl}
                placeholderIcon={placeholderIcon}
                dataTestId={dataTestId}
            />
            <div className="flex flex-col items-start text-left leading-tight">
                <Text fontSize="sm" fontWeight="semibold" className="text-neutral-dark-1000">
                    {title}
                </Text>
                <Text fontSize="xs" fontWeight="regular" className="text-neutral-dark-700">
                    {SUB_LABELS[status]}
                </Text>
            </div>
        </button>
    );
};

/* ——— Subcomponents —————————————————————————————————————————————————————— */

type ThumbnailProps = {
    title: string;
    status: ActiveStatus;
    videoUrl?: string;
    thumbnailUrl?: string;
    placeholderIcon: ReactNode;
    dataTestId: string;
};

function Thumbnail({ title, status, videoUrl, thumbnailUrl, placeholderIcon, dataTestId }: ThumbnailProps): JSX.Element {
    const wrapperClassName =
        'size-10 rounded-sm overflow-hidden bg-neutral-dark-700 relative shrink-0 flex items-center justify-center';

    if (status === 'error') {
        return (
            <div className={wrapperClassName} data-testid={`${dataTestId}-error`}>
                <WarningTriangle className="size-4 dark:[&_path]:fill-neutral-dark-1000" />
            </div>
        );
    }

    return (
        <div className={wrapperClassName}>
            <ThumbnailContent
                title={title}
                status={status}
                videoUrl={videoUrl}
                thumbnailUrl={thumbnailUrl}
                placeholderIcon={placeholderIcon}
                dataTestId={dataTestId}
            />
            {status === 'inProgress' && (
                <div className="absolute inset-0 bg-black/40" data-testid={`${dataTestId}-loading`} />
            )}
            {status === 'success' && (thumbnailUrl || videoUrl) && (
                <div
                    className="absolute inset-0 flex items-center justify-center"
                    data-testid={`${dataTestId}-play-overlay`}
                >
                    <Play className="size-3 dark:[&_path]:fill-neutral-dark-1000" />
                </div>
            )}
        </div>
    );
}

function ThumbnailContent({ title, status, videoUrl, thumbnailUrl, placeholderIcon, dataTestId }: ThumbnailProps): JSX.Element {
    if (thumbnailUrl) {
        return (
            <Image
                src={thumbnailUrl}
                alt={`${title} thumbnail`}
                fill
                sizes="40px"
                className="object-cover"
                data-testid={`${dataTestId}-thumbnail`}
            />
        );
    }

    if (status === 'success' && videoUrl) {
        return (
            <video
                src={videoUrl}
                preload="metadata"
                muted
                playsInline
                className="size-full object-cover"
                data-testid={`${dataTestId}-video-poster`}
            />
        );
    }

    return <span data-testid={`${dataTestId}-placeholder`}>{placeholderIcon}</span>;
}
